/**
 * Valuation Results Component
 * Displays valuation results panel with amount, range, and method details
 */
import React from 'react';
import PropTypes from 'prop-types';
import { DollarSign, Loader, Building2, Info, BarChart3, ArrowRight } from 'lucide-react';

const ValuationResults = ({
  result,
  updateCounter,
  sliderCalculating,
  formData,
  selectedIndustry,
  formatCurrency,
  onShowCalculationModal,
  onNavigateToDashboard,
  onStartNewCalculation
}) => {
  if (!result) {
    // Empty state when no results yet
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-lg border-2 border-blue-300 p-6 sticky top-4">
        <Building2 className="text-blue-600 mb-4" size={48} />
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Ready to Value Your Business?
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          Fill in your business details to get a comprehensive valuation using industry-standard methodologies.
        </p>
        <div className="space-y-2 text-sm text-gray-700">
          <div className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">✓</span>
            <span>5 valuation methods</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">✓</span>
            <span>Real-time discount adjustments</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">✓</span>
            <span>Industry-specific multiples</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">✓</span>
            <span>Instant results with ranges</span>
          </div>
        </div>
      </div>
    );
  }

  // Results state
  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-lg border-2 border-green-300 p-6 sticky top-4">
      <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center gap-2">
        <DollarSign className="text-green-600" size={28} />
        Median Value Results
        {sliderCalculating && (
          <Loader className="animate-spin text-purple-600 ml-auto" size={20} />
        )}
      </h2>

      <div className="mb-6" key={`valuation-${result.timestamp}-${updateCounter}`}>
        <p className="text-sm text-gray-600 mb-2">Median Business Value</p>
        <p className={`text-4xl font-bold text-green-600 transition-opacity duration-300 ${sliderCalculating ? 'opacity-50' : 'opacity-100'}`}>
          {formatCurrency(result.valuation_amount)}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          At {formData.private_company_discount}% private company discount
        </p>
      </div>

      <div className="mb-6" key={`range-${result.timestamp}-${updateCounter}`}>
        <p className="text-sm text-gray-600 mb-2">Median Range of Value</p>
        <p className={`text-lg font-semibold text-gray-800 transition-opacity duration-300 ${sliderCalculating ? 'opacity-50' : 'opacity-100'}`}>
          {formatCurrency(result.low_range)} - {formatCurrency(result.high_range)}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Range represents uncertainty in median value
        </p>

        {/* See Calculation Button */}
        <button
          onClick={onShowCalculationModal}
          className="mt-3 w-full text-sm bg-blue-50 text-blue-700 py-2 px-3 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200 flex items-center justify-center gap-2"
        >
          <Info size={16} />
          See Calculation Details
        </button>
      </div>

      <div className="border-t pt-4 mb-4">
        <p className="text-sm text-gray-600 mb-2">Method Used</p>
        <p className="text-base font-medium text-gray-800 capitalize">
          {result.method === 'cca' ? 'Comparable Company Analysis' :
           result.method === 'dcf' ? 'Discounted Cash Flow' :
           result.method === 'capitalization' ? 'Capitalization of Earnings' :
           result.method === 'nav' ? 'Net Asset Value' :
           result.method === 'manual' ? 'Manual Multiple' : result.method}
        </p>
        {result.primary_method && (
          <p className="text-xs text-gray-500 mt-1">
            Primary multiple: {result.primary_method}
          </p>
        )}
      </div>

      {selectedIndustry && (
        <div className="border-t pt-4 mb-4">
          <p className="text-sm text-gray-600 mb-2">Industry</p>
          <p className="text-base font-medium text-gray-800">
            {selectedIndustry.industry_name}
          </p>
        </div>
      )}

      <div className="border-t pt-4 mb-4">
        <p className="text-sm text-gray-600 mb-2">Private Company Discount</p>
        <p className="text-base font-medium text-purple-600">
          {formData.private_company_discount}%
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Accounts for reduced liquidity vs. public companies
        </p>
      </div>

      <div className="border-t pt-4 mb-4">
        <p className="text-xs text-gray-500">
          Last updated: {new Date(result.timestamp || Date.now()).toLocaleTimeString()}
        </p>
      </div>

      <button
        onClick={onNavigateToDashboard}
        className="w-full mb-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-medium"
      >
        <BarChart3 size={20} />
        View Full Dashboard
        <ArrowRight size={18} />
      </button>

      <button
        onClick={onStartNewCalculation}
        className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
      >
        Start New Calculation
      </button>
    </div>
  );
};

ValuationResults.propTypes = {
  result: PropTypes.object,
  updateCounter: PropTypes.number.isRequired,
  sliderCalculating: PropTypes.bool.isRequired,
  formData: PropTypes.object.isRequired,
  selectedIndustry: PropTypes.object,
  formatCurrency: PropTypes.func.isRequired,
  onShowCalculationModal: PropTypes.func.isRequired,
  onNavigateToDashboard: PropTypes.func.isRequired,
  onStartNewCalculation: PropTypes.func.isRequired
};

export default ValuationResults;
